import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle2, XCircle, Loader2, Users, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import SlotifyLogo from "@/components/SlotifyLogo";

const AcceptInvite = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { session, signIn, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const token = searchParams.get("token");

  const [inviteInfo, setInviteInfo] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<"success" | "error" | null>(null);
  const [errorMsg, setErrorMsg] = useState("");

  // Signup form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Accept state (for already logged-in users)
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setResult("error");
      setErrorMsg(t("invite.invalid"));
      return;
    }

    const fetchInvite = async () => {
      const { data, error } = await supabase.functions.invoke("accept-invite", {
        body: { action: "get_invite", token },
      });
      if (error || data?.error) {
        setResult("error");
        setErrorMsg(t("invite.invalid"));
      } else {
        setInviteInfo(data);
        if (data.status !== "pending") {
          setResult("error");
          setErrorMsg(t("invite.invalid"));
        }
      }
      setLoading(false);
    };

    fetchInvite();
  }, [token]);

  // Register + accept in one step
  const handleRegisterAndAccept = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!acceptedTerms) {
      toast.error(t("auth.accept_terms_error"));
      return;
    }

    setSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke("accept-invite", {
        body: {
          action: "register_and_accept",
          token,
          password,
          first_name: firstName,
          last_name: lastName,
        },
      });

      if (error) throw error;
      if (data?.error) {
        if (data.code === "user_exists") {
          // User already has an account — prompt them to log in
          toast.error(data.error);
          setSubmitting(false);
          return;
        }
        throw new Error(data.error);
      }

      // Sign in with the newly created account
      const { error: signInError } = await signIn(inviteInfo.email, password);
      if (signInError) {
        // Account created but auto-login failed — tell user to log in manually
        toast.success(t("invite.accepted"));
        setTimeout(() => navigate("/login"), 1500);
        return;
      }

      setResult("success");
      setTimeout(() => navigate("/dashboard"), 2000);
    } catch (err: any) {
      toast.error(err.message || t("invite.invalid"));
    } finally {
      setSubmitting(false);
    }
  };

  // Accept invite for already logged-in users
  const handleAccept = async () => {
    if (!token) return;
    setAccepting(true);

    try {
      const { data, error } = await supabase.functions.invoke("accept-invite", {
        body: { action: "accept_invite", token },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setResult("success");
      setTimeout(() => navigate("/dashboard"), 2000);
    } catch (err: any) {
      setResult("error");
      setErrorMsg(err.message || t("invite.invalid"));
    } finally {
      setAccepting(false);
    }
  };

  const orgName = inviteInfo?.organisation_name || "Organisation";
  const roleName = inviteInfo?.role === "admin" ? t("team.role_admin") : t("team.role_member");

  const LeftPanel = () => (
    <div className="hidden w-1/2 bg-gradient-hero lg:block">
      <div className="flex h-full flex-col items-center justify-center p-12">
        <div className="mb-8">
          <SlotifyLogo className="h-10 brightness-0 invert" />
        </div>
        <div className="flex items-center gap-3 mb-4">
          <Users className="h-10 w-10 text-primary-foreground/80" />
        </div>
        <h2 className="text-2xl font-bold text-primary-foreground text-center">
          {t("invite.title")}
        </h2>
        {inviteInfo && (
          <p className="mt-3 text-primary-foreground/60 text-center max-w-sm">
            {t("invite.join_org").replace("{org}", orgName)}
          </p>
        )}
      </div>
    </div>
  );

  if (loading || authLoading) {
    return (
      <div className="flex min-h-screen">
        <LeftPanel />
        <div className="flex w-full items-center justify-center p-8 lg:w-1/2">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (result === "success") {
    return (
      <div className="flex min-h-screen">
        <LeftPanel />
        <div className="flex w-full items-center justify-center p-8 lg:w-1/2">
          <div className="w-full max-w-sm text-center space-y-4">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <CheckCircle2 className="h-8 w-8 text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">{t("invite.accepted")}</h1>
            <p className="text-sm text-muted-foreground">Redirecting to dashboard…</p>
          </div>
        </div>
      </div>
    );
  }

  if (result === "error") {
    return (
      <div className="flex min-h-screen">
        <LeftPanel />
        <div className="flex w-full items-center justify-center p-8 lg:w-1/2">
          <div className="w-full max-w-sm text-center space-y-4">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
              <XCircle className="h-8 w-8 text-destructive" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">{errorMsg}</h1>
            <Button asChild variant="outline">
              <Link to="/">{t("settings.back")}</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Already logged in — check if email matches
  if (session) {
    const emailMatches = session.user.email?.toLowerCase() === inviteInfo?.email?.toLowerCase();

    if (!emailMatches) {
      return (
        <div className="flex min-h-screen relative">
          <div className="absolute top-4 right-4 z-10">
            <LanguageSwitcher />
          </div>
          <LeftPanel />
          <div className="flex w-full items-center justify-center p-8 lg:w-1/2">
            <div className="w-full max-w-sm text-center space-y-4">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
                <XCircle className="h-8 w-8 text-destructive" />
              </div>
              <h1 className="text-lg font-bold text-foreground">
                {t("invite.email_mismatch") !== "invite.email_mismatch"
                  ? t("invite.email_mismatch")
                  : "Email mismatch"}
              </h1>
              <p className="text-sm text-muted-foreground">
                {t("invite.email_mismatch_desc") !== "invite.email_mismatch_desc"
                  ? t("invite.email_mismatch_desc").replace("{email}", inviteInfo?.email || "")
                  : `This invite is for ${inviteInfo?.email}. Please sign in with that email address.`}
              </p>
              <Button onClick={async () => { await supabase.auth.signOut(); window.location.reload(); }} variant="outline" className="w-full">
                {t("auth.sign_out") !== "auth.sign_out" ? t("auth.sign_out") : "Sign out and try again"}
              </Button>
            </div>
          </div>
        </div>
      );
    }

    // Email matches — show accept button
    return (
      <div className="flex min-h-screen relative">
        <div className="absolute top-4 right-4 z-10">
          <LanguageSwitcher />
        </div>
        <LeftPanel />
        <div className="flex w-full items-center justify-center p-8 lg:w-1/2">
          <div className="w-full max-w-sm">
            <div className="mb-8 lg:hidden">
              <SlotifyLogo className="h-8" />
            </div>

            <h1 className="text-2xl font-bold text-foreground">{t("invite.title")}</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {t("invite.join_org").replace("{org}", orgName)}
            </p>

            <div className="mt-6 rounded-lg border border-border bg-muted/50 p-4 space-y-2">
              <p className="text-sm text-foreground">{inviteInfo?.email}</p>
              <p className="text-xs text-muted-foreground">{roleName}</p>
            </div>

            <Button onClick={handleAccept} disabled={accepting} className="w-full mt-6">
              {accepting ? t("invite.accepting") : t("invite.accept")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Not logged in — show inline signup form
  return (
    <div className="flex min-h-screen relative">
      <div className="absolute top-4 right-4 z-10">
        <LanguageSwitcher />
      </div>
      <LeftPanel />
      <div className="flex w-full items-center justify-center p-8 lg:w-1/2">
        <div className="w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <SlotifyLogo className="h-8" />
          </div>

          <h1 className="text-2xl font-bold text-foreground">{t("invite.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("invite.join_org").replace("{org}", orgName)}
          </p>

          <form onSubmit={handleRegisterAndAccept} className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label>{t("auth.email")}</Label>
              <Input
                type="email"
                value={inviteInfo?.email || ""}
                disabled
                className="bg-muted"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="invite-firstName">{t("auth.first_name")}</Label>
                <Input
                  id="invite-firstName"
                  placeholder="John"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="invite-lastName">{t("auth.last_name")}</Label>
                <Input
                  id="invite-lastName"
                  placeholder="Doe"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="invite-password">{t("auth.password")}</Label>
              <Input
                id="invite-password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>

            <div className="flex items-start gap-2">
              <Checkbox
                id="invite-terms"
                checked={acceptedTerms}
                onCheckedChange={(checked) => setAcceptedTerms(checked === true)}
              />
              <label htmlFor="invite-terms" className="text-sm text-muted-foreground leading-snug cursor-pointer">
                {t("auth.accept_terms_pre")}{" "}
                <Link to="/terms" className="font-medium text-primary hover:underline" target="_blank">
                  {t("auth.terms_link")}
                </Link>{" "}
                {t("auth.accept_terms_and")}{" "}
                <Link to="/privacy" className="font-medium text-primary hover:underline" target="_blank">
                  {t("auth.privacy_link")}
                </Link>
              </label>
            </div>

            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? t("invite.accepting") : t("invite.accept")}
              {!submitting && <ArrowRight className="ml-2 h-4 w-4" />}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            {t("auth.have_account")}{" "}
            <Link to={`/login?redirect=/invite?token=${token}`} className="font-medium text-primary hover:underline">
              {t("auth.sign_in")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvite;
